import { useAuth } from '@/src/context/AuthContext';
import { useSubscription } from '@/src/context/SubscriptionContext';
import { useTheme } from '@/src/context/ThemeContext';
import {
  getAllTournamentsUseCase,
  getChallengesReceivedUseCase,
  getFeedUseCase,
  getInvitationsReceivedUseCase,
  getTeamInvitationsUseCase,
  getPendingFriendRequestsUseCase,
  getPublicTournamentsUseCase,
  getUserLeaguesUseCase,
} from '@/src/core/dependencies';
import { League, PostWithDetails, Tournament } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { HomeDashboardScreen } from '@/src/presentation/screens/HomeDashboardScreen';
import { LandingScreen } from '@/src/presentation/screens/LandingScreen';
import { blogPosts } from '@/src/content/blog';
import { APP_STORE_URLS } from '@/src/core/config/appStoreUrls';
import { LEGAL_URLS } from '@/src/core/config/legalUrls';
import { ONBOARDING_FLAG_KEY } from '@/src/core/utils/storageKeys';
import { logger } from '@/src/core/utils';
import { platformAlert } from '@/src/core/utils/platformAlert';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useFocusEffect } from '@react-navigation/native';
import Head from 'expo-router/head';
import { useRouter } from 'expo-router';
import { useCallback, useEffect, useState } from 'react';
import { Linking, Platform } from 'react-native';

export default function HomeDashboardContainer() {
  const router = useRouter();
  const { user } = useAuth();
  const { colors, isDark, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const { checkAccess } = useSubscription();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTournaments, setActiveTournaments] = useState<Tournament[]>([]);
  const [pendingTournamentInvitationsCount, setPendingTournamentInvitationsCount] = useState(0);
  const [pendingTeamInvitationsCount, setPendingTeamInvitationsCount] = useState(0);
  const [pendingFriendRequestsCount, setPendingFriendRequestsCount] = useState(0);
  const [pendingChallengesCount, setPendingChallengesCount] = useState(0);
  const [activeLeagues, setActiveLeagues] = useState<League[]>([]);
  const [recentPosts, setRecentPosts] = useState<PostWithDetails[]>([]);
  const [publicTournaments, setPublicTournaments] = useState<Tournament[]>([]);

  // On first render, check if onboarding should be shown
  useEffect(() => {
    if (!user) return;
    const checkOnboarding = async () => {
      try {
        const show = await AsyncStorage.getItem(ONBOARDING_FLAG_KEY);
        if (show === 'true') {
          router.replace('/onboarding');
        }
      } catch (error) {
        logger.warn('Dashboard', 'Failed to read onboarding flag', { error });
      }
    };
    checkOnboarding();
  }, [user]);

  useFocusEffect(
    useCallback(() => {
      if (user) loadDashboard(false);
    }, [user?.id])
  );

  const loadDashboard = async (isRefresh: boolean) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const [tournaments, invitations, teamInvitations, friendRequests, challenges, leagues, feed, pubTournaments] =
        await Promise.all([
          getAllTournamentsUseCase.execute(20, 0).catch(() => [] as Tournament[]),
          getInvitationsReceivedUseCase.execute(user!.id, 20, 0).catch(() => []),
          getTeamInvitationsUseCase.execute(user!.id).catch(() => ({ received: [], sent: [] })),
          getPendingFriendRequestsUseCase.execute(user!.id, 20, 0).catch(() => []),
          getChallengesReceivedUseCase.execute(user!.id, 20, 0).catch(() => []),
          getUserLeaguesUseCase.execute(user!.id).catch(() => [] as League[]),
          getFeedUseCase.execute(user!.id, 3, 0).catch(() => [] as PostWithDetails[]),
          getPublicTournamentsUseCase.execute(3, 0).catch(() => [] as Tournament[]),
        ]);

      setActiveTournaments(
        tournaments.filter((t) => t.status === 'in_progress').slice(0, 3)
      );
      const pendingTournamentInvitations = invitations.filter((i) => i.status === 'pending').length;
      const pendingTeamInvitations = teamInvitations.received.filter((i) => i.status === 'pending').length;
      setPendingTournamentInvitationsCount(pendingTournamentInvitations);
      setPendingTeamInvitationsCount(pendingTeamInvitations);
      setPendingFriendRequestsCount(friendRequests.length);
      setPendingChallengesCount(challenges.filter((c) => c.status === 'pending').length);
      setActiveLeagues(leagues.filter((l) => l.status !== 'completed').slice(0, 2));
      setRecentPosts(feed.slice(0, 2));
      setPublicTournaments(pubTournaments.slice(0, 3));
    } catch (error) {
      if (error instanceof Error && error.message === 'User not authenticated') return;
      logger.error('Dashboard', 'Failed to load dashboard', { error });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // On web, unauthenticated users see the landing page at the root URL
  if (!user && Platform.OS === 'web') {
    const siteTitle = 'Tournaly — Free Tournament Bracket Maker & Manager';
    const siteDescription =
      'Free tournament bracket maker online. Single, double & triple elimination, round robin, Swiss, King of the Hill. Leagues, standings and social features.';
    const siteUrl = 'https://tournaly.app';
    const ogImage = `${siteUrl}/og-image.png`;
    return (
      <>
        <Head>
          <title>{siteTitle}</title>
          <meta name="description" content={siteDescription} />
          <link rel="canonical" href={siteUrl} />
          <meta property="og:type" content="website" />
          <meta property="og:site_name" content="Tournaly" />
          <meta property="og:title" content={siteTitle} />
          <meta property="og:description" content={siteDescription} />
          <meta property="og:url" content={siteUrl} />
          <meta property="og:image" content={ogImage} />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta property="og:image:alt" content="Tournaly — Free Tournament Bracket Maker & Manager" />
          <meta property="og:locale" content="en_US" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={siteTitle} />
          <meta name="twitter:description" content={siteDescription} />
          <meta name="twitter:image" content={ogImage} />
          <script type="application/ld+json">{JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': [
              {
                '@type': 'WebSite',
                '@id': `${siteUrl}/#website`,
                name: 'Tournaly',
                url: siteUrl,
                description: siteDescription,
                potentialAction: {
                  '@type': 'SearchAction',
                  target: { '@type': 'EntryPoint', urlTemplate: `${siteUrl}/explore?q={search_term_string}` },
                  'query-input': 'required name=search_term_string',
                },
              },
              {
                '@type': 'Organization',
                '@id': `${siteUrl}/#organization`,
                name: 'Tournaly',
                url: siteUrl,
                logo: { '@type': 'ImageObject', url: ogImage, width: 1200, height: 630 },
                sameAs: [],
              },
            ],
          })}</script>
        </Head>
        <LandingScreen
          colors={colors}
          isDark={isDark}
          onToggleTheme={toggleTheme}
          onGetStarted={() => router.push('/auth/register')}
          onSignIn={() => router.push('/auth/login')}
          onSubscribe={() => router.push('/auth/register')}
          onPrivacy={() => Linking.openURL(LEGAL_URLS.privacyPolicy)}
          onTerms={() => Linking.openURL(LEGAL_URLS.termsOfService)}
          onAppStore={() => Linking.openURL(APP_STORE_URLS.appStore)}
          onGooglePlay={() => Linking.openURL(APP_STORE_URLS.googlePlay)}
          latestBlogPosts={blogPosts.slice(0, 3)}
          onBlogPostPress={(slug) => router.push(`/blog/${slug}` as any)}
          onBlogPress={() => router.push('/blog' as any)}
        />
      </>
    );
  }

  const userName =
    user?.user_metadata?.full_name ||
    user?.user_metadata?.name ||
    user?.email?.split('@')[0] ||
    t('navigation.profile');

  return (
    <HomeDashboardScreen
      loading={loading}
      refreshing={refreshing}
      onRefresh={() => loadDashboard(true)}
      userName={userName}
      currentUserId={user?.id ?? ''}
      activeTournaments={activeTournaments}
      pendingTournamentInvitationsCount={pendingTournamentInvitationsCount}
      pendingTeamInvitationsCount={pendingTeamInvitationsCount}
      pendingFriendRequestsCount={pendingFriendRequestsCount}
      pendingChallengesCount={pendingChallengesCount}
      activeLeagues={activeLeagues}
      recentPosts={recentPosts}
      publicTournaments={publicTournaments}
      onCreateTournament={async () => {
        const access = await checkAccess({ feature: 'create_tournament' });
        if (!access.allowed) {
          platformAlert(t('subscription.featureLocked'), t('subscription.tournamentLimitReached'), [
            { text: t('common.cancel'), style: 'cancel' },
            { text: t('subscription.upgradeToPro'), onPress: () => router.push('/paywall') },
          ]);
          return;
        }
        router.push('/create-tournament');
      }}
      onCreateLeague={async () => {
        const access = await checkAccess({ feature: 'create_league' });
        if (!access.allowed) {
          platformAlert(t('subscription.featureLocked'), t('subscription.leagueLimitReached'), [
            { text: t('common.cancel'), style: 'cancel' },
            { text: t('subscription.upgradeToPro'), onPress: () => router.push('/paywall') },
          ]);
          return;
        }
        router.push('/create-league');
      }}
      onTournamentPress={(id) => router.push(`/tournament/${id}`)}
      onLeaguePress={(id) => router.push(`/league/${id}`)}
      onPostUserPress={(userId) => router.push(`/profile/${userId}`)}
      onPostTournamentPress={(tournamentId) => router.push(`/tournament/${tournamentId}`)}
      onSeeAllTournaments={() => router.push('/my-tournaments')}
      onSeeAllLeagues={() => router.push('/leagues')}
      onSeeAllFeed={() => router.push('/feed')}
      onGoToInvitations={() => router.push('/invitations')}
      onGoToFriendRequests={() => router.push('/friend-requests')}
      onGoToChallenges={() => router.push('/challenges')}
      onExplore={() => router.push('/explore')}
      onPublicTournamentPress={(id) => {
        const t = publicTournaments.find(t => t.id === id);
        if (t && t.userId === user?.id) {
          router.push(`/tournament/${id}`);
        } else {
          router.push(`/public/${id}`);
        }
      }}
      colors={colors}
    />
  );
}
